import { Stack } from '@mui/material';
import { Alert, AlertButton, usePersistedDismissable } from '@side/pantry';
import Link from 'next/link';
import { downloadURL } from 'src/utils/downloadURL';
import { useIdentity } from '$api-clients/hooks';
import { useTaxDocuments } from './Download1099Alert.helpers';

const getComponentTexts = (year: number, documents: unknown[]) => {
  const isSingleDoc = documents.length === 1;

  if (isSingleDoc) {
    return {
      title: `Your ${year} 1099 is Ready`,
      description:
        'Your forms are available on your account page. You will also get an email from Track1099.com with a secure link to your form.',
    };
  }

  return {
    title: `${year} 1099 Forms Are Ready`,
    description:
      'Your forms are available on your account page. You will also get an email from Track1099.com with a secure link to your forms.',
  };
};

function getDateWithReplacedYear(date: string, year: number) {
  // Parse the date string directly to avoid timezone conversion
  // Expected format: YYYY-MM-DD or similar ISO format
  const dateParts = date.split('-');
  const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed in Date constructor
  const day = parseInt(dateParts[2], 10);
  // Create a new date in local timezone with the desired year, month, and day
  // This prevents timezone shifts since we're creating it directly in local timezone
  return new Date(year, month, day);
}

export function Download1099Alert() {
  const now = new Date();
  const { data: user } = useIdentity('/auth/userinfo');
  const isLead =
    user?.teams.some(
      (t) => user?.accessibleTeams?.nodes[t.id] && t.roles.includes('LEAD'),
    ) ?? false;
  const isAssociateAgent =
    user?.teams.every(
      (t) => user.accessibleTeams?.nodes[t.id] && t.roles.includes('AGENT'),
    ) ?? false;
  const currentYear = now.getFullYear();
  const documentYear = currentYear - 1;
  const { documents } = useTaxDocuments({ year: documentYear });
  const hasDocuments = documents.length > 0 && documents[0]?.party.id;
  const [shouldShow, { dismiss }] = usePersistedDismissable({
    key: `download-1099-alert-${documentYear}`,
  });
  const texts = getComponentTexts(documentYear, documents);

  // Sunset the alert at the end of May of the current year
  // Dates can be configured via environment variables, with fallback to default dates
  // The year from env vars is ignored and replaced with currentYear
  const startDate = process.env.NEXT_PUBLIC_1099_ALERT_START_DATE
    ? getDateWithReplacedYear(
        process.env.NEXT_PUBLIC_1099_ALERT_START_DATE,
        currentYear,
      )
    : new Date(currentYear, 0, 31); // January 31st
  const endDate = process.env.NEXT_PUBLIC_1099_ALERT_END_DATE
    ? getDateWithReplacedYear(
        process.env.NEXT_PUBLIC_1099_ALERT_END_DATE,
        currentYear,
      )
    : new Date(currentYear, 4, 31, 23, 59, 59, 999); // May 31st (month is 0-indexed, so 4 = May)
  const isInsideRange = now >= startDate && now <= endDate;
  const isLeadOrAssociateAgent = isLead || isAssociateAgent;

  const handleDownload = () => {
    if (documents[0]?.party.id) {
      const url = new URL(
        `/payment/api/tax-documents/${documents[0].party.id}?year=${documentYear}`,
        window.location.origin,
      ).href;
      downloadURL(url);
    }
  };

  if (!shouldShow || !isInsideRange || !hasDocuments || !isLeadOrAssociateAgent)
    return null;

  return (
    <Alert
      onClose={dismiss}
      severity="primary"
      variant="stacked"
      square
      title={texts.title}
      action={
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          sx={{
            gap: 2,
          }}
        >
          {isAssociateAgent && documents.length === 1 && (
            <AlertButton
              variant="outlined"
              severity="primary"
              onClick={handleDownload}
            >
              Download
            </AlertButton>
          )}
          <AlertButton
            variant="contained"
            severity="primary"
            component={Link}
            href="/user/account?section=tax-documents"
          >
            View
          </AlertButton>
        </Stack>
      }
    >
      {texts.description}
    </Alert>
  );
}
